import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {
  IntegrationSettingsTypesEnum,
  IntercomIntegrationSettingsModel,
} from '../../../../../../models/api/integrationModel';
import {
  signInToIntegrationAccountAction,
  signOutOfIntegrationAsyncAction,
} from '../../../../../../store/data/integrations/integrationsActions';
import {getIsUserGlobalAdmin} from '../../../../../../store/data/teammates/teammatesSelectors';

/*
 * Props.
 */

interface SettingsAppsConfigureIntercomProps {
  configurationSettings?: IntercomIntegrationSettingsModel;
}

/*
 * Style.
 */

const StyledDescriptionWrapperDiv = styled.div`
  margin-bottom: 16px;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsAppsConfigureIntercom: FC<SettingsAppsConfigureIntercomProps> = (props) => {
  const {configurationSettings} = props;
  const dispatch = useAppDispatch();
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const onSignInClick = () => {
    dispatch(signInToIntegrationAccountAction(IntegrationSettingsTypesEnum.INTERCOM, 'Intercom'));
  };

  const onSignOutClick = () => {
    dispatch(signOutOfIntegrationAsyncAction(IntegrationSettingsTypesEnum.INTERCOM));
  };

  return (
    <>
      <StyledDescriptionWrapperDiv>
        <Trans id="settings-apps-configure-app-dialog-intercom-description">
          Displays contact information alongside conversations.
        </Trans>
      </StyledDescriptionWrapperDiv>
      {maybeRenderConnectedDetails(configurationSettings, isGlobalAdmin, onSignOutClick)}
      {maybeRenderNotConnectedDetails(configurationSettings, isGlobalAdmin, onSignInClick)}
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderConnectedDetails(
  configurationSettings: IntercomIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignOutClick: Handler,
) {
  // If we do not have a user_name and app_id we can assume we are not logged in properly.
  if (
    !configurationSettings ||
    !configurationSettings.readonly?.account?.user_name ||
    !configurationSettings.readonly?.account?.app_id
  ) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-intercom-connected-description">
        Your company is connected as <strong>{configurationSettings.readonly.account.user_name}</strong> for
        the app ID <strong>{configurationSettings.readonly.account.app_id}</strong>.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button
          variant={ButtonVariantsEnum.SECONDARY_DANGER}
          onClick={onSignOutClick}
          isDisabled={!isGlobalAdmin}
        >
          <Trans id="settings-apps-configure-app-dialog-intercom-sign-out-button">Sign out of Intercom</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}

function maybeRenderNotConnectedDetails(
  configurationSettings: IntercomIntegrationSettingsModel | undefined,
  isGlobalAdmin: boolean,
  onSignInClick: Handler,
) {
  if (
    configurationSettings?.readonly?.account?.user_name &&
    configurationSettings?.readonly?.account?.app_id
  ) {
    return null;
  }
  return (
    <>
      <Trans id="settings-apps-configure-app-dialog-intercom-not-connected-description">
        Connect to enable see contact information form Intercom in the side bar.
      </Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onSignInClick} isDisabled={!isGlobalAdmin}>
          <Trans id="settings-apps-configure-app-dialog-intercom-sign-in-button">Sign in to Intercom</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </>
  );
}
